import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import AthleteLayout from "../common/AthleteLayout";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { AthleteHeader } from "../common/AthleteHeader";
import API_URL from "../../api/API";
import "chart.js/auto";

const ArrowD = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 14 8" fill="none">
    <path d="M13 1L7 7L1 1" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const trainingFocuses = ["Technical", "Speed - Strength", "Power", "Strength - Speed"];

export const AthleteLoadvelocityPerformance = () => {
  const { athlete } = useAthleteAuthContext();
  const { template_id } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [performanceData, setPerformanceData] = useState({});
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    const fetchPerformanceData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = {};
        for (const focus of trainingFocuses) {
          const res = await fetch(`${API_URL.GET_ATHLETE_LOADVELOCITY_PERFORMANCE}${athlete.id}/${focus}`, {
            headers: { Authorization: `Bearer ${athlete.token}` }
          });
          if (!res.ok) throw new Error("Failed to fetch " + focus);
          const json = await res.json();
          data[focus] = json.data || {}; // { "Week 1": [{ day, speed_data: [...] }, ...], ... }
        }
        setPerformanceData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (athlete?.id && athlete?.token) fetchPerformanceData();
  }, [athlete?.id, athlete?.token]);

  // Flatten all speed_data per day into one continuous line
  const prepareChartData = (focus) => {
    if (!performanceData[focus]) return { labels: [], datasets: [] };
  
    const labels = [];
    const speeds = [];
  
    Object.keys(performanceData[focus]).forEach((weekName) => {
      const weekNumberMatch = weekName.match(/Week\s*(\d+)/i);
      const weekNumber = weekNumberMatch ? weekNumberMatch[1] : "";
  
      performanceData[focus][weekName].forEach((dayObj) => {
        const shortDay = dayObj.day ? dayObj.day.slice(0, 3) : "";
  
        // ✅ Take the highest (max) speed of the day instead of average
        const daySpeeds = Array.isArray(dayObj.speed_data) ? dayObj.speed_data : [];
        const highestSpeed = daySpeeds.length > 0 ? Math.max(...daySpeeds) : 0;
  
        labels.push(`W${weekNumber} - ${shortDay}`);
        speeds.push(highestSpeed);
      });
    });
  
    return {
      labels,
      datasets: [
        {
          label: `${focus} Speed (Highest Rep)`,
          data: speeds,
          borderColor: "#1E88E5",
          backgroundColor: "rgba(30,136,229,0.15)",
          tension: 0.25,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  };
  
  
  

  const chartOptions = {
    responsive: true,
    plugins: { legend: { display: true } },
    scales: {
      x: { ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 } },
      y: { beginAtZero: true, title: { display: true, text: "Speed" } }
    }
  };

  const toggleAccordion = (index) => setOpenIndex(openIndex === index ? null : index);

  return (
    <AthleteLayout>
      <div className="bg-[#f6f6f6] h-full">
        <div className="bg-white"><AthleteHeader /></div>

        <div className="relative h-full bg-[#f6f6f6] pb-5 overflow-y-auto">
          <div className="bg-[#F6F6F6]">
            <div className="w-full max-w-2xl mx-auto flex flex-row justify-between gap-5 px-10 py-[19px]">
              <span onClick={() => navigate("/athlete/tools/loadvelocity/test/result")} className="text-base font-normal pb-[4px] text-[#a5a5a5] cursor-pointer">Test</span>
              <span onClick={() => navigate("/athlete/tools/loadvelocity/training-weeks/" + template_id)} className="text-base font-normal pb-[4px] text-[#a5a5a5] cursor-pointer">Train</span>
              <span className="text-base font-normal pb-[4px] text-black border-b border-black">Performance</span>
            </div>
          </div>

          <div className="w-full max-w-2xl mx-auto mb-[30px]">
            {trainingFocuses.map((focus, idx) => (
              <div key={idx} className={`relative border-b border-black/20 last:border-none ${openIndex === idx ? "!border-none" : ""}`}>
                <div className={`${openIndex === idx ? "!bg-[#ECECEC]" : ""} flex flex-col py-[15px] px-5 bg-transparent cursor-pointer`}>
                  <button className="text-black text-start text-base font-normal flex flex-row justify-between gap-3 items-center" onClick={() => toggleAccordion(idx)}>
                    <span>{focus.replace(/([A-Z])/g, " $1").trim()}</span>
                    <span className={openIndex === idx ? "rotate-180" : ""}><ArrowD /></span>
                  </button>

                  {openIndex === idx && (
                    <div className="pt-[15px] pb-3">
                      {isLoading ? <p>Loading...</p> :
                        error ? <p className="text-red-500">{error}</p> :
                        <Line data={prepareChartData(focus)} options={chartOptions} />
                      }
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AthleteLayout>
  );
};

export default AthleteLoadvelocityPerformance;
